import React, { useState, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const GridContainer = styled.div`
  padding: 12px;
  min-height: 200px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  width: 100%;
  position: relative;
`;

const ProductList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  width: 100%;
`;

const ProductItem = styled.div`
  border: 1px solid #e0e0e0;
  padding: 10px;
  text-align: center;
  background: white;
  cursor: grab;
  user-select: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  touch-action: none;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  min-width: 0;
  position: relative;

  ${props => props.isSelected && `
    border: 2px solid #007bff;
    background: #e3f2fd;
    box-shadow: 0 2px 8px rgba(0,123,255,0.3);
  `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    border-color: ${props => props.isSelected ? '#007bff' : '#000000'};
  }

  ${props => props.isDragging && `
    opacity: 0.8;
    transform: rotate(2deg);
  `}
`;

const SelectionBadge = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  z-index: 10;
`;

const SelectionRectangle = styled.div`
  position: absolute;
  border: 2px dashed #007bff;
  background: rgba(0, 123, 255, 0.1);
  pointer-events: none;
  z-index: 100;
`;

const GroupCounter = styled.div`
  margin-bottom: 10px;
  font-size: 14px;
  color: #007bff;
  font-weight: bold;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const ProductCount = styled.div`
  margin-bottom: 20px;
  font-size: 14px;
  color: #666;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const ProductImage = styled.img`
  max-width: 100%;
  max-height: 220px;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const ProductId = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: #000000;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
`;

const ProductCode = styled.div`
  margin: 12px 0 8px;
  font-size: 13px;
  color: #666;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const PriorityInfo = styled.div`
  font-size: 12px;
  color: #666;
  margin: 5px 0;
  text-align: center;
`;

const SortableItem = React.memo(({ product, isSelected, onSelect, selectionIndex, isDragging }) => {
  React.useEffect(() => {
    if (!product.iconUrl) {
      console.warn(`Produkt ID:${product.id} nie ma URL zdjęcia!`);
    }
  }, [product]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: product.sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleImageError = (e) => {
    console.error(`Błąd ładowania zdjęcia dla produktu ID:${product.id}, URL:${product.iconUrl}`);
    e.target.src = 'https://via.placeholder.com/150?text=Brak+zdjęcia';
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd + klik = dodaj/usuń z zaznaczenia
      onSelect(product.id, 'toggle');
    } else if (e.shiftKey) {
      // Shift + klik = zaznacz zakres
      onSelect(product.id, 'range');
    } else {
      // Zwykły klik = zaznacz tylko ten element
      onSelect(product.id, 'single');
    }
  };

  return (
    <ProductItem
      ref={setNodeRef}
      style={style}
      isSelected={isSelected}
      isDragging={isDragging}
      onClick={handleClick}
      {...attributes}
      {...listeners}
    >
      {isSelected && <SelectionBadge>{selectionIndex}</SelectionBadge>}
      <ImageContainer>
        <ProductImage 
          src={product.iconUrl || 'https://via.placeholder.com/150?text=Brak+zdjęcia'} 
          alt={`Product ${product.id}`}
          loading="lazy"
          onError={handleImageError}
        />
      </ImageContainer>
      <PriorityInfo>
        Priorytet: {product.priority}
      </PriorityInfo>
      <ProductCode>
        Index: {product.codeOnCard}
      </ProductCode>
      <ProductId>ID: {product.id}</ProductId>
    </ProductItem>
  );
});

function ProductGrid({ products, onProductsReorder, selectedNode }) {
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null);
  const gridRef = useRef(null);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8, // Trzeba przeciągnąć przynajmniej 8px żeby aktywować drag
    }
  });
  const touchSensor = useSensor(TouchSensor);
  
  const sensors = useSensors(mouseSensor, touchSensor);

  React.useEffect(() => {
    if (products && products.length > 0) {
      const productsWithoutImages = products.filter(p => !p.iconUrl);
      if (productsWithoutImages.length > 0) {
        console.warn(`${productsWithoutImages.length} z ${products.length} produktów nie ma URL zdjęcia`);
      }
    }
  }, [products]);

  const sortableItems = React.useMemo(() => 
    products.map(product => ({
      ...product,
      sortableId: `sortable-${product.id}`
    }))
  , [products]);

  // Funkcja do obliczania kolizji prostokąta zaznaczenia z produktami
  const getProductsInSelectionRect = useCallback((rect) => {
    if (!gridRef.current) return [];
    
    const productElements = gridRef.current.querySelectorAll('[data-product-id]');
    const selectedIds = [];
    
    productElements.forEach(element => {
      const elementRect = element.getBoundingClientRect();
      const gridRect = gridRef.current.getBoundingClientRect();
      
      const elementRelativeRect = {
        left: elementRect.left - gridRect.left,
        top: elementRect.top - gridRect.top,
        right: elementRect.right - gridRect.left,
        bottom: elementRect.bottom - gridRect.top
      };
      
      // Sprawdź czy prostokąty się przecinają
      if (rect.left < elementRelativeRect.right &&
          rect.right > elementRelativeRect.left &&
          rect.top < elementRelativeRect.bottom &&
          rect.bottom > elementRelativeRect.top) {
        selectedIds.push(element.getAttribute('data-product-id'));
      }
    });
    
    return selectedIds;
  }, []);

  // Obsługa zaznaczania prostokątem
  const handleMouseDown = (e) => {
    // Tylko jeśli kliknięto w tło (nie w produkt)
    if (e.target === e.currentTarget) {
      const rect = gridRef.current.getBoundingClientRect();
      const startX = e.clientX - rect.left;
      const startY = e.clientY - rect.top;
      
      setIsSelecting(true);
      setSelectionStart({ x: startX, y: startY });
      setSelectionEnd({ x: startX, y: startY });
      
      // Jeśli nie ma Ctrl/Cmd, wyczyść poprzednie zaznaczenie
      if (!e.ctrlKey && !e.metaKey) {
        setSelectedProducts(new Set());
      }
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isSelecting && selectionStart) {
      const rect = gridRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      
      setSelectionEnd({ x: currentX, y: currentY });
      
      // Oblicz prostokąt zaznaczenia
      const selectionRectangle = {
        left: Math.min(selectionStart.x, currentX),
        top: Math.min(selectionStart.y, currentY),
        right: Math.max(selectionStart.x, currentX),
        bottom: Math.max(selectionStart.y, currentY)
      };
      
      setSelectionRect(selectionRectangle);
      
      // Znajdź produkty w prostokącie i zaznacz je
      const productsInRect = getProductsInSelectionRect(selectionRectangle);
      setSelectedProducts(prev => {
        const newSelection = new Set(prev);
        productsInRect.forEach(id => newSelection.add(id));
        return newSelection;
      });
    }
  }, [isSelecting, selectionStart, getProductsInSelectionRect]);

  const handleMouseUp = useCallback(() => {
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setSelectionRect(null);
  }, []);

  // Event listeners dla zaznaczania prostokątem
  React.useEffect(() => {
    if (isSelecting) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isSelecting, handleMouseMove, handleMouseUp]);

  const handleProductSelect = useCallback((productId, mode) => {
    const productIndex = products.findIndex(p => p.id === productId);
    
    setSelectedProducts(prev => {
      const newSelection = new Set(prev);
      
      switch (mode) {
        case 'single':
          newSelection.clear();
          newSelection.add(productId);
          setLastSelectedIndex(productIndex);
          break;
          
        case 'toggle':
          if (newSelection.has(productId)) {
            newSelection.delete(productId);
          } else {
            newSelection.add(productId);
            setLastSelectedIndex(productIndex);
          }
          break;
          
        case 'range':
          if (lastSelectedIndex !== null) {
            const start = Math.min(lastSelectedIndex, productIndex);
            const end = Math.max(lastSelectedIndex, productIndex);
            
            // Zaznacz zakres
            for (let i = start; i <= end; i++) {
              newSelection.add(products[i].id);
            }
          } else {
            newSelection.clear();
            newSelection.add(productId);
          }
          setLastSelectedIndex(productIndex);
          break;
      }
      
      return newSelection;
    });
  }, [products, lastSelectedIndex]);

  const handleDragEnd = React.useCallback((event) => {
    const { active, over } = event;
    
    if (active && over && active.id !== over.id) {
      const activeProductId = sortableItems.find(p => p.sortableId === active.id)?.id;
      
      if (selectedProducts.has(activeProductId) && selectedProducts.size > 1) {
        // Grupowe przeciąganie
        const selectedProductIds = Array.from(selectedProducts);
        const selectedItems = products.filter(p => selectedProductIds.includes(p.id));
        const nonSelectedItems = products.filter(p => !selectedProductIds.includes(p.id));
        
        const overIndex = sortableItems.findIndex(p => p.sortableId === over.id);
        const targetProduct = sortableItems[overIndex];
        const targetIndex = products.findIndex(p => p.id === targetProduct.id);
        
        // Wstaw grupę w nowej pozycji
        const newProducts = [...nonSelectedItems];
        newProducts.splice(targetIndex, 0, ...selectedItems);
        
        onProductsReorder(newProducts);
      } else {
        // Pojedyncze przeciąganie
        const oldIndex = sortableItems.findIndex(p => p.sortableId === active.id);
        const newIndex = sortableItems.findIndex(p => p.sortableId === over.id);
        onProductsReorder(arrayMove(products, oldIndex, newIndex));
      }
    }
  }, [sortableItems, products, onProductsReorder, selectedProducts]);

  // Klawisze skrótów
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedProducts(new Set());
        setLastSelectedIndex(null);
      } else if (e.ctrlKey || e.metaKey) {
        if (e.key === 'a') {
          e.preventDefault();
          // Ctrl+A = zaznacz wszystkie
          setSelectedProducts(new Set(products.map(p => p.id)));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [products]);

  // Kliknięcie w tło = odznacz wszystkie (tylko jeśli nie zaznaczamy prostokątem)
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget && !isSelecting) {
      setSelectedProducts(new Set());
      setLastSelectedIndex(null);
    }
  };

  if (!products?.length) {
    return <GridContainer>Wybierz kategorię, aby zobaczyć produkty</GridContainer>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <GridContainer 
        ref={gridRef} 
        onClick={handleBackgroundClick}
        onMouseDown={handleMouseDown}
        style={{ cursor: isSelecting ? 'crosshair' : 'default' }}
      >
        <ProductCount>
          Liczba produktów w kategorii: {products.length}
        </ProductCount>
        
        {selectedProducts.size > 0 && (
          <GroupCounter>
            Zaznaczone produkty: {selectedProducts.size}
            {selectedProducts.size > 1 && " (można przeciągnąć grupowo)"}
            <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
              Ctrl+klik: dodaj/usuń • Shift+klik: zakres • Ctrl+A: wszystkie • Esc: odznacz • Przeciągnij: zaznacz prostokątem
            </div>
          </GroupCounter>
        )}
        
        {/* Prostokąt zaznaczenia */}
        {selectionRect && (
          <SelectionRectangle
            style={{
              left: selectionRect.left,
              top: selectionRect.top,
              width: selectionRect.right - selectionRect.left,
              height: selectionRect.bottom - selectionRect.top
            }}
          />
        )}
        
        <SortableContext
          items={sortableItems.map(p => p.sortableId)}
          strategy={verticalListSortingStrategy}
        >
          <ProductList>
            {sortableItems.map((product, index) => {
              const isSelected = selectedProducts.has(product.id);
              const selectionIndex = isSelected ? 
                Array.from(selectedProducts).indexOf(product.id) + 1 : null;
              
              return (
                <div key={product.sortableId} data-product-id={product.id}>
                  <SortableItem
                    product={{
                      ...product,
                      priority: product.menuItems.find(item => item.textId === selectedNode)?.level || 'brak'
                    }}
                    isSelected={isSelected}
                    onSelect={handleProductSelect}
                    selectionIndex={selectionIndex}
                  />
                </div>
              );
            })}
          </ProductList>
        </SortableContext>
      </GridContainer>
    </DndContext>
  );
}

export default React.memo(ProductGrid); 